import { useMemo, useRef } from "react";

import dayjs from "dayjs";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { getSoftwareMetadataByName } from "@synthesis/shared-services/reporting/reportGenerationModelsController/client";
import type { ModelSoftwareInfoViewDto } from "@synthesis/shared-services/reporting/reportGenerationModelsController/types";
import { IconArrowLeft, IconDownload } from "@synthesis/shared-icons";
import { Button, Table } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { useDownloadModelSoftwareDetailsReport } from "../../../../entity/reporting";

import { defaultColDef, tableColumnsDetailedFileReport } from "../reports.lib";

export const DetailedFileReportPage = () => {
  const gridApi = useRef<unknown>(null);
  const navigate = useNavigate();
  const { softwareName } = useSearch({ strict: false }) as { softwareName?: string };
  const { mutate: downloadDetails } = useDownloadModelSoftwareDetailsReport(softwareName);

  const serverSideDatasource = useMemo(
    () => ({
      getRows: async (params: { success: (value: { rowData: unknown[]; rowCount: number; rowCountKnown: boolean }) => void; fail: () => void }) => {
        try {
          if (!softwareName) {
            params.fail();
            return;
          }
          const data = (await getSoftwareMetadataByName({ path: { softwareName } })).data;
          const rowData = data.map((item: ModelSoftwareInfoViewDto) => ({
            modelName: item.modelName ?? "",
            modelSize: item.modelSize ?? "",
            softwareName: item.softwareInfo?.softwareName ?? "",
            softwareVersion: `${item.softwareInfo?.softwareName ?? ""} ${item.softwareInfo?.softwareVersion ?? ""}`,
            createdAt: item.softwareInfo?.createdAt ? dayjs(item.softwareInfo.createdAt).format("DD.MM.YYYY") : "",
            fileCreatedAt: item.softwareInfo?.fileCreatedAt ? dayjs(item.softwareInfo.fileCreatedAt).format("DD.MM.YYYY") : "",
            organizationName: item.organizationName ?? "",
            projectNumber: item.projectNumber ?? "",
            projectName: item.projectName ?? "",
            projectStatus: item.projectStatus ?? "",
            modelVersion: item.modelVersion ? `Версия ${item.modelVersion}` : "",
          }));
          params.success({ rowData, rowCount: data.length, rowCountKnown: true });
        } catch (error) {
          logger.error("Error fetching detailed file report:", error);
          params.fail();
        }
      },
    }),
    [softwareName],
  );

  const onGridReady = (params: { api: unknown }) => {
    gridApi.current = params.api;
  };

  return (
    <main className="relative flex h-full flex-col">
      <h1 className="heading-h4 flex flex-col gap-4 rounded-xl px-8 pb-6 pt-8">Детальный отчёт по файлам {softwareName}</h1>
      <div className="mx-8 mb-8 flex h-full flex-col gap-4 rounded-2xl bg-white px-6 py-8">
        <div className="flex justify-between">
          <Button
            className="w-fit"
            theme="text"
            leftIcon={<IconArrowLeft className="size-5" />}
            onClick={() => navigate({ to: "/reports/statistics-usage-software" })}
          >
            Назад
          </Button>
          <Button size="sm" className="ml-auto" leftIcon={<IconDownload className="size-5" />} onClick={() => downloadDetails()}>
            Экспортировать отчёт в .xlsx
          </Button>
        </div>
        <div className="flex size-full h-full grow flex-col">
          <Table
            isSortable
            hasFilters={false}
            tableColumns={tableColumnsDetailedFileReport}
            defaultColDef={defaultColDef}
            rowModelType="serverSide"
            serverSideDatasource={serverSideDatasource}
            onGridReady={onGridReady}
            module="gge"
          />
        </div>
      </div>
    </main>
  );
};
